import { SyntheticEvent, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import { useAppDispatch } from 'app/hooks';
import { FormMsg, NoFormMsg, doFormMsg } from 'components/forms/FormMessage';
import { ModalDialog, ModalDialogLink } from 'components/modal/Modal';
import { useModal } from 'components/modal/ModalContext';
import { navigateIn } from 'components/navigation/Navigation';
import { resetPwdAsync } from 'features/user/userSlice';
import { escapeRegEx } from 'helpers/helpers';
import { getPattern } from 'helpers/regex';
import { useUser } from 'selectors/userSelectors';
import { ShowPasswordButton } from './ShowPasswordButton';
import UserLoginForm from './UserLoginForm';
import UserSignupForm from './UserSignupForm';

export default function UserResetPwdForm() {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useUser();
  const { closeModal } = useModal();

  const [data, setData] = useState({
    password: '',
    password2: '',
    token: '',
  });
  const [hasReset, setHasReset] = useState<boolean>(false);
  const [validated, setValidated] = useState(false);
  const { token } = useParams();
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [showPassword, setShowPassword] = useState(false);

  // we use the token parameter from the url to receive the token (see App.js)
  useEffect(() => {
    if (token) {
      setData({ ...data, token: token });
    } else {
      closeModal();
      navigate('/');
    }
  }, [token]);

  // This would be nice, but it's not working because
  // this form is opened by openModal and rendered in a portal,
  // not directly in the tree.
  // if (!token && !user) return <Navigate to={'/'} replace />;
  if (!token && !user) return null;

  function handleChangeData(e: SyntheticEvent) {
    const target = e.target as HTMLInputElement;
    setValidated(false);
    if (target.form && target.name === 'password') {
      target.form.password2.pattern = escapeRegEx(target.value);
    }
    if (target.name === 'password2') setValidated(true);
    setData({
      ...data,
      [target.name]: target.value,
    });
  }

  async function handleSubmit(): Promise<boolean | number> {
    return new Promise((resolve, reject) => {
      try {
        if (hasReset) {
          resolve(true);
          navigate('/');
        } else if (data.password !== data.password2) {
          setValidated(true);
          doFormMsg(
            {
              message: intl.formatMessage({ id: 'USERS.RESET.NOMATCH' }),
              success: false,
            },
            setFormMsg,
          );
          resolve(false);
        } else {
          setValidated(false);
          dispatch(resetPwdAsync(data)).then((response: { payload: any }) => {
            switch (response.payload?.response.status) {
              case 500:
                doFormMsg(
                  {
                    message: intl.formatMessage({ id: 'USERS.RESET.FAILED' }),
                    success: false,
                  },
                  setFormMsg,
                );
                reject(intl.formatMessage({ id: 'USERS.RESET.FAILED' }));
                break;
              default:
                doFormMsg(
                  {
                    message: intl.formatMessage({
                      id: 'USERS.RESET.SUCCESS',
                    }),
                    success: true,
                  },
                  setFormMsg,
                );
                setHasReset(true);
                navigateIn('/', 15000, navigate);
                resolve(false); // keep modal open
                break;
            }
          });
        }
      } catch (err) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'USERS.RESET.FAILED' }),
            success: false,
          },
          setFormMsg,
        );
        console.log('Request failed for unclear reason.', err);
        reject();
      }
    });
  }

  return (
    <ModalDialog
      className="user_form narrow"
      title={<FormattedMessage id="USERS.RESET.FORM_TITLE" />}
      okBtn={{
        label: intl.formatMessage({
          id: hasReset ? 'X.OK' : 'USERS.RESET.SUBMIT',
        }),
        onClick: handleSubmit,
      }}
      enableEscape
      formMsg={formMsg}
      validated={validated}
    >
      <Form.Group as={Row} controlId="Password">
        <Form.Label column sm="12" md="12" lg="12">
          <FormattedMessage id="USERS.RESET.PASSWORD" />
        </Form.Label>
        <Col sm={12} md={12} lg={12}>
          <Row className="subrow">
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="password"
              value={data.password || ''}
              onChange={handleChangeData}
              required
              pattern={getPattern('password')}
            />
            <ShowPasswordButton
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />
          </Row>
          <Form.Text className="text-muted instruction">
            <FormattedMessage id="USERS.SIGNUP.PASSWORD_HINT" />
          </Form.Text>
          <Form.Control.Feedback type="invalid">
            <FormattedMessage id="USERS.SIGNUP.PASSWORD_FDBCK" />
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="Password2">
        <Form.Label column sm="12" md="12" lg="12">
          <FormattedMessage id="USERS.RESET.PASSWORD2" />
        </Form.Label>
        <Col sm={12} md={12} lg={12}>
          <Row className="subrow">
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              name="password2"
              onChange={handleChangeData}
              required
            />
            <ShowPasswordButton
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />
          </Row>
          <Form.Text className="text-muted instruction"></Form.Text>
          <Form.Control.Feedback type="invalid">
            <div className="feedback">
              <FormattedMessage id="USERS.SIGNUP.PASSWORD_FDBCK" />
            </div>
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="form">
        <Col align={'left'} sm={12}>
          <div className="vspaced">
            <FormattedMessage id="USERS.LOGIN.OTHER_OPTIONS" />
            <ModalDialogLink component={UserLoginForm} closeAll>
              <FormattedMessage id="USERS.RESET.REMEMBERED" />
            </ModalDialogLink>
            <ModalDialogLink component={UserSignupForm} closeAll>
              <FormattedMessage id="USERS.LOGIN.NO_ACCOUNT" />
            </ModalDialogLink>
          </div>
        </Col>
      </Form.Group>
    </ModalDialog>
  );
}
